// components/achievements/LeaderboardTab.jsx
// Achievement Leaderboard with Opt-In Flow
// Created: Dec 30, 2025 - Task 3.7
// Updated: Dec 30, 2025 - Tiered monthly bonus system
// ============================================
// - Default: OFF (users must opt-in)
// - One-time bonus: 25 tokens for joining
// - TIERED Monthly bonus based on rank:
//   - Top 10: 200 tokens ($20)
//   - Top 50: 150 tokens ($15)
//   - Top 100: 100 tokens ($10)
//   - Everyone else: 50 tokens ($5)
// - Privacy toggle available on page and in Settings

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { 
  Trophy, 
  Crown, 
  Coins,
  Loader2,
  RefreshCw,
  Users,
  Star,
  Gift,
  Shield,
  Eye,
  EyeOff,
  ChevronRight,
  Award,
  TrendingUp,
  CheckCircle,
  Flame,
  Target
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTokens } from '../../context/TokenContext';
import { useNotification } from '../../context/NotificationContext';

// Tiered monthly bonus structure - TOP 100 ONLY
const MONTHLY_BONUS_TIERS = [
  { maxRank: 10, tokens: 200, value: '$20.00', label: 'Top 10', color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-300' },
  { maxRank: 50, tokens: 150, value: '$15.00', label: 'Top 50', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-300' },
  { maxRank: 100, tokens: 100, value: '$10.00', label: 'Top 100', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-300' }
];

// Default tier for users outside top 100
const DEFAULT_BONUS_TIER = { 
  maxRank: Infinity, 
  tokens: 50, 
  value: '$5.00', 
  label: 'Participant', 
  color: 'text-green-600', 
  bg: 'bg-green-50', 
  border: 'border-green-300' 
};

// Get bonus tier based on rank (default tier if outside top 100)
const getBonusTier = (rank) => {
  if (!rank || rank > 100) return DEFAULT_BONUS_TIER;
  return MONTHLY_BONUS_TIERS.find(tier => rank <= tier.maxRank);
};

// GraphQL Queries
const GET_ACHIEVEMENT_LEADERBOARD = gql`
  query GetAchievementLeaderboard($limit: Int) {
    achievementLeaderboard(limit: $limit) {
      userId
      name
      profileImage
      achievementCount
      totalTokensEarned
    }
  }
`;

const GET_LEADERBOARD_STATUS = gql`
  query GetLeaderboardStatus {
    leaderboardOptIn
  }
`;

// GraphQL Mutations
const JOIN_LEADERBOARD = gql`
  mutation JoinLeaderboard {
    joinLeaderboard {
      success
      showOnLeaderboard
      bonusAwarded
      message
    }
  }
`;

const TOGGLE_LEADERBOARD = gql`
  mutation ToggleLeaderboardVisibility($show: Boolean!) {
    toggleLeaderboardVisibility(show: $show) {
      success
      showOnLeaderboard
      message
    }
  }
`;

const LeaderboardTab = () => {
  const { user } = useAuth();
  const { refreshBalance } = useTokens();
  const { addNotification } = useNotification();
  const [showOptOutConfirm, setShowOptOutConfirm] = useState(false);
  
  // Check if user has opted in to leaderboard
  const { 
    data: statusData, 
    loading: statusLoading,
    refetch: refetchStatus 
  } = useQuery(GET_LEADERBOARD_STATUS, {
    fetchPolicy: 'network-only'
  });
  
  // Fetch leaderboard data
  const {
    data: leaderboardData,
    loading: leaderboardLoading,
    refetch: refetchLeaderboard
  } = useQuery(GET_ACHIEVEMENT_LEADERBOARD, {
    variables: { limit: 20 },
    fetchPolicy: 'cache-and-network',
    skip: !statusData?.leaderboardOptIn // Only fetch if opted in
  });
  
  // Join leaderboard mutation
  const [joinLeaderboard, { loading: joining }] = useMutation(JOIN_LEADERBOARD, {
    onCompleted: (data) => {
      if (data.joinLeaderboard.success) {
        refetchStatus();
        refetchLeaderboard();
        refreshBalance?.();
        
        addNotification({
          type: 'success',
          title: '🎉 Welcome to the Leaderboard!',
          message: data.joinLeaderboard.bonusAwarded 
            ? `You earned 25 bonus tokens! Plus 10 tokens every month you stay.`
            : data.joinLeaderboard.message,
          showToast: true
        });
      }
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message,
        showToast: true
      });
    }
  });
  
  // Toggle leaderboard visibility mutation
  const [toggleLeaderboard, { loading: toggling }] = useMutation(TOGGLE_LEADERBOARD, {
    onCompleted: (data) => {
      if (data.toggleLeaderboardVisibility.success) {
        refetchStatus();
        setShowOptOutConfirm(false);
        
        addNotification({
          type: data.toggleLeaderboardVisibility.showOnLeaderboard ? 'success' : 'info',
          title: data.toggleLeaderboardVisibility.showOnLeaderboard 
            ? 'Back on the Leaderboard!' 
            : 'Removed from Leaderboard',
          message: data.toggleLeaderboardVisibility.message,
          showToast: true
        });
      }
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message,
        showToast: true
      });
    }
  });
  
  const isOptedIn = statusData?.leaderboardOptIn === true;
  const leaderboard = leaderboardData?.achievementLeaderboard || [];
  
  // Find current user's position
  const currentUserEntry = leaderboard.find(entry => entry.userId === user?.id);
  const currentUserRank = currentUserEntry 
    ? leaderboard.findIndex(entry => entry.userId === user?.id) + 1 
    : null;
  
  // Calculate current and next bonus tier
  const currentBonusTier = useMemo(() => getBonusTier(currentUserRank), [currentUserRank]);
  
  const nextBonusTier = useMemo(() => {
    if (!currentUserRank) return null;
    const currentTierIndex = MONTHLY_BONUS_TIERS.findIndex(tier => currentUserRank <= tier.maxRank);
    if (currentTierIndex <= 0) return null; // Already at top tier
    return MONTHLY_BONUS_TIERS[currentTierIndex - 1];
  }, [currentUserRank]);
  
  const ranksToNextTier = useMemo(() => {
    if (!currentUserRank || !nextBonusTier) return 0;
    return currentUserRank - nextBonusTier.maxRank;
  }, [currentUserRank, nextBonusTier]);
  
  // Loading state
  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }
  
  // ==========================================
  // OPT-IN VIEW (User has not joined yet)
  // ==========================================
  if (!isOptedIn) {
    return (
      <div className="space-y-4">
        {/* Hero Card */}
        <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 text-white rounded-xl p-6 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Achievement Leaderboard</h2>
          <p className="text-white/90 mb-4">
            Compete with other members and show off your achievements!
          </p>
        </div>
        
        {/* Benefits Card */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-green-500" />
            Join & Earn Tokens
          </h3>
          
          <div className="space-y-3">
            {/* One-time bonus */}
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-green-800">25 tokens instantly</p>
                <p className="text-sm text-green-600">One-time bonus for joining</p>
              </div>
              <span className="text-lg font-bold text-green-600">$2.50</span>
            </div>
            
            {/* Tiered Monthly Bonus */}
            <div className="p-3 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-5 h-5 text-purple-600" />
                <p className="font-medium text-purple-800">Monthly Bonus by Rank</p>
              </div>
              
              <div className="space-y-2">
                {MONTHLY_BONUS_TIERS.map((tier, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-2 rounded-lg ${tier.bg} border ${tier.border}`}
                  >
                    <div className="flex items-center gap-2">
                      {index === 0 && <Crown className={`w-4 h-4 ${tier.color}`} />}
                      {index === 1 && <Trophy className={`w-4 h-4 ${tier.color}`} />}
                      {index === 2 && <Award className={`w-4 h-4 ${tier.color}`} />}
                      {index === 3 && <Star className={`w-4 h-4 ${tier.color}`} />}
                      <span className={`text-sm font-medium ${tier.color}`}>{tier.label}</span>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold ${tier.color}`}>{tier.tokens} tokens</span>
                      <span className="text-xs text-gray-500 ml-1">({tier.value}/mo)</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-purple-600 mt-3 text-center">
                Climb the leaderboard to earn bigger rewards!
              </p>
            </div>
          </div>
        </div>
        
        {/* What's Shown Card */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-500" />
            What Others Will See
          </h3>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Your name</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Profile picture (if set)</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Achievement count</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Total tokens earned</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600">
                <strong>Your privacy is protected.</strong> Personal info (email, phone, address) is never shown. 
                Other members cannot contact you unless you choose to share your info.
              </p>
            </div>
          </div>
        </div>
        
        {/* Join Button */}
        <button
          onClick={() => joinLeaderboard()}
          disabled={joining}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:from-yellow-600 hover:to-orange-600 transition-all disabled:opacity-50 shadow-lg"
        >
          {joining ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Joining...
            </>
          ) : (
            <>
              <Trophy className="w-5 h-5" />
              Join Leaderboard & Earn 25 Tokens
            </>
          )}
        </button>
        
        <p className="text-center text-xs text-gray-500">
          You can leave the leaderboard anytime in Settings
        </p>
      </div>
    );
  }
  
  // ==========================================
  // LEADERBOARD VIEW (User has opted in)
  // ==========================================
  return (
    <div className="space-y-4">
      {/* Header with Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Achievement Leaderboard
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              refetchLeaderboard();
              addNotification({
                type: 'info',
                title: 'Refreshed',
                message: 'Leaderboard updated',
                showToast: true
              });
            }}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowOptOutConfirm(true)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <EyeOff className="w-3 h-3" />
            Leave
          </button>
        </div>
      </div>
      
      {/* Your Rank Card (if on leaderboard) */}
      {currentUserEntry && (
        <div className={`bg-gradient-to-r ${
          currentUserRank <= 10 ? 'from-yellow-500 to-orange-500' :
          currentUserRank <= 50 ? 'from-purple-500 to-indigo-500' :
          currentUserRank <= 100 ? 'from-blue-500 to-cyan-500' :
          'from-green-500 to-teal-500'
        } text-white rounded-xl p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
                #{currentUserRank}
              </div>
              <div>
                <p className="font-semibold">Your Rank</p>
                <p className="text-sm text-white/80">
                  {currentUserEntry.achievementCount} achievements • {currentUserEntry.totalTokensEarned || 0} tokens
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/70">Monthly Bonus</p>
              <p className="font-bold flex items-center gap-1 text-lg">
                <Coins className="w-4 h-4" />
                +{currentBonusTier.tokens}
              </p>
              <p className="text-xs text-white/80">{currentBonusTier.value}/mo</p>
            </div>
          </div>
          
          {/* Next tier progress */}
          {nextBonusTier && (
            <div className="mt-3 pt-3 border-t border-white/20">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/80">
                  <Target className="w-4 h-4 inline mr-1" />
                  {ranksToNextTier} more rank{ranksToNextTier !== 1 ? 's' : ''} to {nextBonusTier.label}
                </span>
                <span className="font-medium">+{nextBonusTier.tokens - currentBonusTier.tokens} more tokens/mo</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Loading State */}
      {leaderboardLoading && !leaderboard.length ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-6 text-center">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No one on the leaderboard yet</p>
          <p className="text-xs text-gray-400 mt-1">Be the first to earn achievements!</p>
        </div>
      ) : (
        <>
          {/* Top 3 Podium */}
          <div className="flex items-end justify-center gap-2 py-4 bg-gradient-to-b from-yellow-50 to-white rounded-xl">
            {/* 2nd Place */}
            {leaderboard[1] && (
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 border-2 border-gray-300 mx-auto mb-1 overflow-hidden">
                  {leaderboard[1].profileImage ? (
                    <img src={leaderboard[1].profileImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg font-bold text-gray-400">
                      {leaderboard[1].name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <span className="text-2xl">🥈</span>
                <p className="text-xs font-medium text-gray-700 truncate max-w-[70px]">
                  {leaderboard[1].name || 'Anonymous'}
                </p>
                <p className="text-[10px] text-gray-500">{leaderboard[1].achievementCount} 🏆</p>
              </div>
            )}
            
            {/* 1st Place */}
            {leaderboard[0] && (
              <div className="text-center -mt-4">
                <div className="w-18 h-18 rounded-full bg-yellow-100 border-4 border-yellow-400 mx-auto mb-1 overflow-hidden ring-4 ring-yellow-200" style={{ width: '72px', height: '72px' }}>
                  {leaderboard[0].profileImage ? (
                    <img src={leaderboard[0].profileImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xl font-bold text-yellow-600">
                      {leaderboard[0].name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <span className="text-3xl">🥇</span>
                <p className="text-sm font-bold text-gray-900 truncate max-w-[80px]">
                  {leaderboard[0].name || 'Anonymous'}
                </p>
                <p className="text-xs text-yellow-600 font-medium">{leaderboard[0].achievementCount} 🏆</p>
                <div className="flex items-center justify-center gap-1 text-xs text-yellow-600">
                  <Coins className="w-3 h-3" />
                  {leaderboard[0].totalTokensEarned || 0}
                </div>
              </div>
            )}
            
            {/* 3rd Place */}
            {leaderboard[2] && (
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-amber-50 border-2 border-amber-300 mx-auto mb-1 overflow-hidden">
                  {leaderboard[2].profileImage ? (
                    <img src={leaderboard[2].profileImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg font-bold text-amber-400">
                      {leaderboard[2].name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <span className="text-2xl">🥉</span>
                <p className="text-xs font-medium text-gray-700 truncate max-w-[70px]">
                  {leaderboard[2].name || 'Anonymous'}
                </p>
                <p className="text-[10px] text-gray-500">{leaderboard[2].achievementCount} 🏆</p>
              </div>
            )}
          </div>
          
          {/* Rest of Leaderboard */}
          {leaderboard.length > 3 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {leaderboard.slice(3).map((entry, index) => {
                const rank = index + 4;
                const isCurrentUser = entry.userId === user?.id;
                
                return (
                  <div 
                    key={entry.userId}
                    className={`px-4 py-3 flex items-center gap-3 ${
                      index !== leaderboard.length - 4 ? 'border-b border-gray-100' : ''
                    } ${isCurrentUser ? 'bg-blue-50' : ''}`}
                  >
                    {/* Rank */}
                    <div className="w-8 text-center">
                      <span className="text-sm font-bold text-gray-500">#{rank}</span>
                    </div>
                    
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                      {entry.profileImage ? (
                        <img src={entry.profileImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg font-bold text-gray-400">
                          {entry.name?.[0]?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isCurrentUser ? 'text-blue-700' : 'text-gray-900'}`}>
                        {entry.name || 'Anonymous'}
                        {isCurrentUser && <span className="text-xs text-blue-500 ml-1">(You)</span>}
                      </p>
                      <p className="text-xs text-gray-500">
                        {entry.achievementCount} achievements
                      </p>
                    </div>
                    
                    {/* Tokens */}
                    <div className="flex items-center gap-1 text-sm">
                      <Coins className="w-4 h-4 text-yellow-500" />
                      <span className="font-medium text-gray-700">{entry.totalTokensEarned || 0}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      
      {/* Monthly Bonus Info */}
      <div className={`${currentBonusTier.bg} border ${currentBonusTier.border} rounded-xl p-4`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            currentUserRank && currentUserRank <= 10 ? 'bg-yellow-100' :
            currentUserRank && currentUserRank <= 50 ? 'bg-purple-100' :
            currentUserRank && currentUserRank <= 100 ? 'bg-blue-100' :
            'bg-green-100'
          }`}>
            <Gift className={`w-5 h-5 ${currentBonusTier.color}`} />
          </div>
          <div className="flex-1">
            <p className={`font-medium ${currentBonusTier.color.replace('text-', 'text-').replace('-600', '-800')}`}>
              {currentUserRank ? `${currentBonusTier.label} Bonus Active` : 'Monthly Bonus Active'}
            </p>
            <p className={`text-sm ${currentBonusTier.color}`}>
              You're earning {currentBonusTier.tokens} tokens ({currentBonusTier.value}) every month!
            </p>
          </div>
        </div>
        
        {/* Tier breakdown reminder */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600 mb-2">Monthly bonus tiers:</p>
          <div className="flex flex-wrap gap-2">
            {MONTHLY_BONUS_TIERS.map((tier, index) => (
              <span 
                key={index} 
                className={`text-xs px-2 py-1 rounded-full ${
                  currentUserRank && currentUserRank <= tier.maxRank && 
                  (index === 0 || currentUserRank > MONTHLY_BONUS_TIERS[index - 1].maxRank)
                    ? `${tier.bg} ${tier.color} font-bold ring-2 ring-offset-1 ${tier.border.replace('border-', 'ring-')}`
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {tier.label}: {tier.value}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      {/* Opt-Out Confirmation Modal */}
      {showOptOutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <EyeOff className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Leave Leaderboard?</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              You'll be removed from the public leaderboard and <strong>lose your monthly bonus</strong>.
            </p>
            
            {currentUserRank && (
              <div className={`${currentBonusTier.bg} border ${currentBonusTier.border} rounded-lg p-3 mb-4`}>
                <p className={`text-sm font-medium ${currentBonusTier.color}`}>
                  You're currently earning {currentBonusTier.tokens} tokens ({currentBonusTier.value}/month) as a {currentBonusTier.label} member.
                </p>
              </div>
            )}
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                💡 You can rejoin anytime, but you won't receive another join bonus.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowOptOutConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
              >
                Stay
              </button>
              <button
                onClick={() => toggleLeaderboard({ variables: { show: false } })}
                disabled={toggling}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {toggling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Leave
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardTab;